import classNames from "classnames";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import CustomSelect from "shared/components/common/customSelect";
import { QuestionnaireStep9VS } from "shared/utils/validation";
import ShowMoreText from "react-show-more-text";
import styles from "./style.module.scss";
import { GetQuestionStatement, hasChanges } from "shared/utils/helper";
import { GetAppOutcomesService } from "shared/services/questionnaire";
import { JobRole } from "shared/utils/enum";

interface formikValuesInterface {
  jobs: QuestionnaireStep9Type[];
}

interface QuestionnaireStep9Props {
  formikRef: any;
  formData: any;
  setLoading: (loading: boolean) => void;
  setAddEvent: (val: boolean) => void;
}

const QuestionnaireStep9 = ({
  formikRef,
  formData,
  setLoading,
  setAddEvent,
}: QuestionnaireStep9Props) => {
  const initialValues: formikValuesInterface = {
    jobs: [
      {
        jobTitle: "",
        outcomes: {},
        definition: "",
        output: "",
      },
    ],
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: QuestionnaireStep9VS,
    onSubmit: () => {},
  });

  const { values, touched, errors } = formik;

  useEffect(() => {
    formikRef.current = formik;
    // eslint-disable-next-line
  }, [formik]);

  useEffect(() => {
    let jobs = formData;
    let fields: any = [];
    for (let k = 0; k < jobs?.length; k++) {
      let obj = {
        jobTitle: jobs?.[k]?.jobTitle,
        outcomes: jobs?.[k]?.q8?.outcomes,
        definition: jobs?.[k]?.q8?.definition ? jobs?.[k]?.q8?.definition : "",
        output: jobs?.[k]?.q8?.output,
      };
      fields.push(obj);
    }
    formik.setFieldValue("jobs", fields);
    // eslint-disable-next-line
  }, [formData]);

  useEffect(() => {
    if (hasChanges(values.jobs, initialValues.jobs?.[0])) {
      setAddEvent?.(true);
    }
    // eslint-disable-next-line
  }, [values.jobs]);

  return (
    <div className={classNames(styles.stepContainer)}>
      <div className={classNames(styles.head)}>
        <p>Outcomes</p>
        <h1>
          <span>Q8.</span> {GetQuestionStatement(8)}
        </h1>
      </div>
      <div className={classNames(styles.tableWrapper)}>
        <table className="table" style={{ minWidth: "800px", margin: "0px" }}>
          <thead>
            <tr style={{ height: "44px" }}>
              <th className={classNames(styles.tableHead)}>Job Title</th>
              <th className={classNames(styles.tableHead)}>
                Outcomes <span>*</span>
              </th>
              <th className={classNames(styles.tableHead)}>Definition</th>
              <th className={classNames(styles.tableHead)}>RAS Output</th>
            </tr>
          </thead>
          <tbody>
            {values.jobs.map((job, index) => {
              return (
                <RowItem
                  job={job}
                  index={index}
                  key={index}
                  formik={formik}
                  formData={formData}
                  touched={touched}
                  errors={errors}
                  setLoading={setLoading}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RowItem = ({
  job,
  touched,
  index,
  formik,
  formData,
  errors,
  setLoading,
}: any) => {
  const [outcomesOptions, setOutcomesOptions] = useState<any[]>([]);

  const handleOptions = () => {
    let jobs = formData;
    let dropOptions: any[] = [];

    let options = jobs[index]?.q1?.alldropDowns?.filter(
      (itm: any, inx: number) => itm?.dropdown_id === 8
    )[0]?.option;
    let roleType = jobs[index]?.roleType?.value;
    for (let j = 0; j < options?.length; j++) {
      let option = options[j];
      let obj = {
        title: option?.short_des,
        Icon: null,
        action: (index: number) => {
          formik.setFieldValue(
            `jobs[${index}].definition`,
            option?.long_des ? option?.long_des : ""
          );
          formik.setFieldValue(`jobs[${index}].outcomes`, {
            name: option?.short_des,
            value: option?.id,
            level: option?.level,
          });

          if (roleType === JobRole.management) {
            setLoading(true);
            GetAppOutcomesService({
              level: jobs?.[index]?.q6?.applicationLevel?.level,
              outcome: option?.level,
            })
              .then(({ data }) => {
                formik.setFieldValue(
                  `jobs[${index}].output`,
                  data?.outcome ? data?.outcome : "0"
                );
                formik.setFieldTouched(`jobs[${index}].outcomes`, false);
              })
              .finally(() => {
                setLoading(false);
              });
          } else {
            formik.setFieldValue(
              `jobs[${index}].output`,
              option?.score ? option?.score : "0"
            );
            formik.setFieldTouched(`jobs[${index}].outcomes`, false);
          }
        },
      };
      dropOptions.push(obj);
    }

    setOutcomesOptions(dropOptions);
  };

  useEffect(() => {
    handleOptions();
    // eslint-disable-next-line
  }, []);

  return (
    <tr>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "15%" }}
      >
        <div className={classNames(styles.titleContainer)}>
          <label>{job.jobTitle}</label>
        </div>
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "35%" }}
      >
        <CustomSelect
          placeholder="Select Outcomes"
          options={outcomesOptions}
          customInputContainer={classNames(styles.selectionContainer)}
          notRequiredMargin
          customOptionsContainer={classNames(styles.optionsContainer)}
          value={job.outcomes?.name}
          error={
            touched.jobs?.[index]?.outcomes &&
            //@ts-ignore
            errors.jobs?.[index]?.outcomes
          }
          warning={job.outcomes?.warning ? "Improbable combination" : ""}
          tabIndex={index}
        />
      </td>

      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "40%" }}
      >
        <div className={classNames(styles.descContainer)}>
          <ShowMoreText
            lines={3}
            more="Show more"
            less="Show less"
            expanded={false}
            anchorClass="show-more-less-clickable"
            truncatedEndingComponent={"... "}
          >
            <label>{job.definition}</label>
          </ShowMoreText>
        </div>
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "10%" }}
      >
        <div className={classNames(styles.scoreContainer)}>
          <label>{job.output ? job.output : 0}</label>
        </div>
      </td>
    </tr>
  );
};

export default QuestionnaireStep9;
